/**
 * Root attribute present while touch is the pointer in use. A finger leaves
 * `:hover` on whatever it last tapped until something else is tapped, so hover
 * styling applies only without this attribute: the Tailwind `hover:` variant
 * checks it, and stylesheets gate `:hover` with
 * `:where(html:not([data-touch-input]))`. With it, the `touch:` variant shows
 * what hover would otherwise reveal. It starts from the primary pointer and then
 * follows each press or move, so a laptop touchscreen switches with the hand in use.
 */
export const TOUCH_INPUT_ATTRIBUTE = "data-touch-input"

export function installTouchInput(target: Document) {
  const root = target.documentElement
  const follow = (event: PointerEvent) => {
    // A synthetic pointer event can carry no type; it says nothing about the hand.
    if (!event.pointerType) return
    const touch = event.pointerType === "touch"
    if (touch !== root.hasAttribute(TOUCH_INPUT_ATTRIBUTE)) root.toggleAttribute(TOUCH_INPUT_ATTRIBUTE, touch)
  }
  root.toggleAttribute(TOUCH_INPUT_ATTRIBUTE, target.defaultView?.matchMedia("(hover: none)").matches ?? false)
  // Capture: a press must count before its own handlers run and before the
  // browser moves `:hover` to the tapped element.
  const options = { capture: true, passive: true }
  target.addEventListener("pointerdown", follow, options)
  target.addEventListener("pointermove", follow, options)
  return () => {
    target.removeEventListener("pointerdown", follow, options)
    target.removeEventListener("pointermove", follow, options)
    root.removeAttribute(TOUCH_INPUT_ATTRIBUTE)
  }
}
